import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import type { TranslateFunction } from "web_myplann/Shared/TranslateFunction";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";
import { optionalDateAsString } from "web_myplann/Shared/Validation/optionalDateAsString";
import { optionalNonNegativeInteger } from "web_myplann/Shared/Validation/optionalNonNegativeInteger";
import { optionalZipCode } from "web_myplann/Shared/Validation/optionalZipCode";
import { refineRequiredMinimum } from "web_myplann/Shared/Validation/refineRequiredMinimum";
import { trimmedNullable } from "web_myplann/Shared/Validation/trimmedNullable";
import { ValidationLimits } from "web_myplann/Shared/Validation/validationLimits";
import { z } from "zod";

export const landMinimumGroup = [
	"location",
	"locationMunicipality",
	"locationZipCode",
	"description",
] as const;

export const getLandFormSchema = (t: TranslateFunction) => {
	const maxMessage = t(Resources.Portfolio.AddShared.Validations.maxValue);
	const integerMessages = {
		integerMessage: t(Resources.Portfolio.AddLand.Validations.wholeNumber),
		maxMessage,
	};
	const maxLength = (max: number) =>
		t(Resources.Portfolio.AddShared.Validations.maxLength, { max });

	return z
		.object({
			name: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.LongName,
						maxLength(ValidationLimits.TextLength.LongName),
					),
			),
			location: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.Location,
						maxLength(ValidationLimits.TextLength.Location),
					),
			),
			locationMunicipality: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.Short,
						maxLength(ValidationLimits.TextLength.Short),
					),
			),
			locationZipCode: optionalZipCode(
				t(Resources.Portfolio.AddLand.Validations.zipFormat),
			),
			parcelNumber: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.Short,
						maxLength(ValidationLimits.TextLength.Short),
					),
			),
			landSize: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			landValue: optionalNonNegativeInteger(
				ValidationLimits.Amount.Max,
				integerMessages,
			),
			purchaseDate: optionalDateAsString(DateFormat.Iso),
			description: trimmedNullable(
				z
					.string()
					.max(
						ValidationLimits.TextLength.Note,
						maxLength(ValidationLimits.TextLength.Note),
					),
			),
			forInvestments: z.boolean(),
		})
		.superRefine(refineRequiredMinimum(landMinimumGroup));
};

export type LandFormInput = z.input<ReturnType<typeof getLandFormSchema>>;
export type LandFormOutput = z.output<ReturnType<typeof getLandFormSchema>>;
